/**
 * uiux-renewal 오리파 팩 도메인 목 데이터 — Random Pack(§4.2 가치형 오리파,
 * 1 pull = 1 card) 7티어 상품(가격 사다리 1k~100k RP) + §4.11.1 공개 9항목(가격·총/남은 팩·카드 풀·
 * 확률·그룹 구성·최저/평균/최고 가치·재매입 비율) 표시용. 가격 티어는 스펙 Q7
 * (Starter $25 ~ Baller $999, 100 RP = 1 USDT) 기반, 확률·가치·잔여는 전부 mock.
 * 개봉은 api.pullAgentCardGacha(tier, count, agentId?, packKind=pack.id)로 연결.
 * TODO(server): 팩 상품 목록/상세 RPC·잔여 실시간 RPC·확률표 서버 값으로 교체.
 */
import type { CardFinish } from "@/lib/api";
import { HOME_AGENTS } from "@/lib/homeAgents";

// ------------------------------------------------------------------- Types

/** §4.2 카드 그룹 4단 — 그룹↔피니시 매핑은 lib/cards.ts 8피니시 재사용 */
export type OripaGroup = "common" | "chase" | "grail" | "super_grail";

export const ORIPA_GROUP_LABEL: Record<OripaGroup, string> = {
  common: "Common",
  chase: "Chase",
  grail: "Grail",
  super_grail: "Super Grail",
};

export type OripaGroupConfig = {
  group: OripaGroup;
  /** 그룹에 매핑된 피니시 — lib/cards.ts CARD_FINISHES 키와 1:1 */
  finishes: CardFinish[];
  /** 그룹 카드 풀 장수 = finishes × 6 에이전트 (packCardPool과 일치) */
  cardCount: number;
  /** 그룹 배출 확률 % — 팩 내 합 100 (mock) */
  oddsPct: number;
};

/** Value Distribution 1행 — maxRp null = 상한 개방("N+") */
export type OripaValueBand = {
  minRp: number;
  maxRp: number | null;
  /** 확률 % — 팩 내 합 100 (mock) */
  pct: number;
};

export type OripaPack = {
  /** api.pullAgentCardGacha 의 packKind 슬러그 */
  id: string;
  name: string;
  /** mock — 100 RP = 1 USDT (스펙 Q7) */
  priceRp: number;
  /** TODO(server): 총/잔여 팩 실시간 RPC */
  totalPacks: number;
  remainingPacks: number;
  /** §4.2 — 1 pull = 1 card 고정 */
  cardsPerPull: 1;
  /** §4.2 재매입 80~90% — 기획 확정 90% (pricing-odds-plan §3) */
  buybackPct: number;
  groups: OripaGroupConfig[];
  /** §4.11.1 Value Distribution — RP 가치 구간 × 확률 % (mock) */
  valueBands: OripaValueBand[];
  /** §4.11.1 최저/평균/최고 가치 (RP, mock 스냅샷) */
  lowestRp: number;
  averageRp: number;
  highestRp: number;
};

// ------------------------------------------------------------------- Packs

// §4.12/F-06 카드 풀 분리 — Ultra Chase /1(padparadscha_sapphire, gold_vinyl)은
// Season 1 Box 전용(shop.ts ULTRA_CHASE_CARDS). 팩 최고 피니시는 black_ice_prizms.
// 그룹↔피니시 매핑은 팩 가격에 맞춰 팩별로 상이 (가치 정합 — pricing-odds-plan §4).

const AGENTS = HOME_AGENTS.length; // 6

/**
 * 가격 사다리·확률 — docs/pricing-odds-plan.md (courtyard.io 벤치마크).
 * 전 팩 공통: P(≤팩가) 76% / 초과 24%(대부분 1.1P 미만) / 바닥 = 한 단계 아래 팩가 /
 * EV 0.97P / 재매입 90%. totalPacks는 grail 공급(시리얼 재고 ÷ 0.6%) 이내.
 * TODO(server): 상품 목록 RPC — 전 수치 mock
 */
export const ORIPA_PACKS: OripaPack[] = [
  {
    id: "ember",
    name: "Ember Pack",
    priceRp: 1_000,
    totalPacks: 1_500,
    remainingPacks: 1_034,
    cardsPerPull: 1,
    buybackPct: 90,
    groups: [
      { group: "common", finishes: ["base"], cardCount: 1 * AGENTS, oddsPct: 76 },
      { group: "chase", finishes: ["holo"], cardCount: 1 * AGENTS, oddsPct: 21 },
      { group: "grail", finishes: ["blue_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 3 },
    ],
    valueBands: [
      { minRp: 500, maxRp: 750, pct: 42 },
      { minRp: 750, maxRp: 1_000, pct: 34 },
      { minRp: 1_000, maxRp: 1_100, pct: 15 },
      { minRp: 1_100, maxRp: 2_000, pct: 6 },
      { minRp: 2_000, maxRp: 5_000, pct: 2.4 },
      { minRp: 5_000, maxRp: null, pct: 0.6 },
    ],
    lowestRp: 500,
    averageRp: 970,
    highestRp: 20_000,
  },
  {
    id: "starter",
    name: "Spark Pack",
    priceRp: 2_500,
    totalPacks: 1_000,
    remainingPacks: 613,
    cardsPerPull: 1,
    buybackPct: 90,
    groups: [
      { group: "common", finishes: ["base", "holo"], cardCount: 2 * AGENTS, oddsPct: 76 },
      { group: "chase", finishes: ["blue_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 21 },
      { group: "grail", finishes: ["gold_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 3 },
    ],
    valueBands: [
      { minRp: 1_000, maxRp: 1_875, pct: 42 },
      { minRp: 1_875, maxRp: 2_500, pct: 34 },
      { minRp: 2_500, maxRp: 2_750, pct: 15 },
      { minRp: 2_750, maxRp: 5_000, pct: 6 },
      { minRp: 5_000, maxRp: 12_500, pct: 2.4 },
      { minRp: 12_500, maxRp: null, pct: 0.6 },
    ],
    lowestRp: 1_000,
    averageRp: 2_425,
    highestRp: 50_000,
  },
  {
    id: "comet",
    name: "Comet Pack",
    priceRp: 5_000,
    totalPacks: 800,
    remainingPacks: 548,
    cardsPerPull: 1,
    buybackPct: 90,
    groups: [
      { group: "common", finishes: ["base", "holo"], cardCount: 2 * AGENTS, oddsPct: 76 },
      { group: "chase", finishes: ["blue_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 21 },
      { group: "grail", finishes: ["gold_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 2.4 },
      { group: "super_grail", finishes: ["red_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 0.6 },
    ],
    valueBands: [
      { minRp: 2_500, maxRp: 3_750, pct: 42 },
      { minRp: 3_750, maxRp: 5_000, pct: 34 },
      { minRp: 5_000, maxRp: 5_500, pct: 15 },
      { minRp: 5_500, maxRp: 10_000, pct: 6 },
      { minRp: 10_000, maxRp: 25_000, pct: 2.4 },
      { minRp: 25_000, maxRp: null, pct: 0.6 },
    ],
    lowestRp: 2_500,
    averageRp: 4_850,
    highestRp: 100_000,
  },
  {
    id: "collector",
    name: "Nova Pack",
    priceRp: 10_000,
    totalPacks: 500,
    remainingPacks: 374,
    cardsPerPull: 1,
    buybackPct: 90,
    groups: [
      { group: "common", finishes: ["base", "holo"], cardCount: 2 * AGENTS, oddsPct: 76 },
      { group: "chase", finishes: ["blue_ice_sapphire", "gold_ice_sapphire"], cardCount: 2 * AGENTS, oddsPct: 21 },
      { group: "grail", finishes: ["red_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 2.4 },
      { group: "super_grail", finishes: ["black_ice_prizms"], cardCount: 1 * AGENTS, oddsPct: 0.6 },
    ],
    valueBands: [
      { minRp: 5_000, maxRp: 7_500, pct: 42 },
      { minRp: 7_500, maxRp: 10_000, pct: 34 },
      { minRp: 10_000, maxRp: 11_000, pct: 15 },
      { minRp: 11_000, maxRp: 20_000, pct: 6 },
      { minRp: 20_000, maxRp: 50_000, pct: 2.4 },
      { minRp: 50_000, maxRp: null, pct: 0.6 },
    ],
    lowestRp: 5_000,
    averageRp: 9_700,
    highestRp: 200_000,
  },
  {
    id: "premium",
    name: "Eclipse Pack",
    priceRp: 25_000,
    totalPacks: 300,
    remainingPacks: 168,
    cardsPerPull: 1,
    buybackPct: 90,
    groups: [
      { group: "common", finishes: ["blue_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 76 },
      { group: "chase", finishes: ["gold_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 21 },
      { group: "grail", finishes: ["red_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 2.4 },
      { group: "super_grail", finishes: ["black_ice_prizms"], cardCount: 1 * AGENTS, oddsPct: 0.6 },
    ],
    valueBands: [
      { minRp: 10_000, maxRp: 18_750, pct: 42 },
      { minRp: 18_750, maxRp: 25_000, pct: 34 },
      { minRp: 25_000, maxRp: 27_500, pct: 15 },
      { minRp: 27_500, maxRp: 50_000, pct: 6 },
      { minRp: 50_000, maxRp: 125_000, pct: 2.4 },
      { minRp: 125_000, maxRp: null, pct: 0.6 },
    ],
    lowestRp: 10_000,
    averageRp: 24_250,
    highestRp: 500_000,
  },
  {
    id: "quasar",
    name: "Quasar Pack",
    priceRp: 50_000,
    totalPacks: 120,
    remainingPacks: 77,
    cardsPerPull: 1,
    buybackPct: 90,
    groups: [
      { group: "common", finishes: ["gold_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 76 },
      { group: "chase", finishes: ["red_ice_sapphire"], cardCount: 1 * AGENTS, oddsPct: 21 },
      { group: "grail", finishes: ["black_ice_prizms"], cardCount: 1 * AGENTS, oddsPct: 3 },
    ],
    valueBands: [
      { minRp: 25_000, maxRp: 37_500, pct: 42 },
      { minRp: 37_500, maxRp: 50_000, pct: 34 },
      { minRp: 50_000, maxRp: 55_000, pct: 15 },
      { minRp: 55_000, maxRp: 100_000, pct: 6 },
      { minRp: 100_000, maxRp: 250_000, pct: 2.4 },
      { minRp: 250_000, maxRp: null, pct: 0.6 },
    ],
    lowestRp: 25_000,
    averageRp: 48_500,
    highestRp: 600_000,
  },
  {
    id: "baller",
    name: "Sovereign Pack",
    priceRp: 100_000,
    totalPacks: 60,
    remainingPacks: 9,
    cardsPerPull: 1,
    buybackPct: 90,
    groups: [
      { group: "common", finishes: ["gold_ice_sapphire", "red_ice_sapphire"], cardCount: 2 * AGENTS, oddsPct: 79 },
      { group: "super_grail", finishes: ["black_ice_prizms"], cardCount: 1 * AGENTS, oddsPct: 21 },
    ],
    valueBands: [
      { minRp: 50_000, maxRp: 75_000, pct: 42 },
      { minRp: 75_000, maxRp: 100_000, pct: 34 },
      { minRp: 100_000, maxRp: 110_000, pct: 15 },
      { minRp: 110_000, maxRp: 200_000, pct: 6 },
      { minRp: 200_000, maxRp: 500_000, pct: 2.4 },
      { minRp: 500_000, maxRp: null, pct: 0.6 },
    ],
    lowestRp: 50_000,
    averageRp: 97_000,
    highestRp: 600_000,
  },
];

export function findOripaPack(packId: string | undefined): OripaPack | undefined {
  return ORIPA_PACKS.find((pack) => pack.id === packId);
}

// ---------------------------------------------------------------- Card pool

export type OripaPoolEntry = {
  group: OripaGroup;
  finish: CardFinish;
  agentId: string;
};

/** 팩 카드 풀 전개 — 그룹×피니시×6에이전트. CARDS 레일(등급별 가로 레일) 데이터 소스. */
export function packCardPool(pack: OripaPack): OripaPoolEntry[] {
  return pack.groups.flatMap((config) =>
    config.finishes.flatMap((finish) =>
      HOME_AGENTS.map((agent) => ({
        group: config.group,
        finish,
        agentId: agent.agentId,
      })),
    ),
  );
}

// -------------------------------------------------------------- Recent pulls

export type RecentPull = {
  id: string;
  /** ORIPA_PACKS id */
  packId: string;
  agentId: string;
  finish: CardFinish;
  /** 오픈 에디션(base/holo)은 null */
  serial: number | null;
  /** 표시용 상대시간 (mock) — 서버 연결 시 timestamp로 교체 */
  pulledAgo: string;
};

/** TODO(server): 실시간 개봉 피드 RPC(전역 TOP PULLS·팩별 필터) — 전부 mock */
export const RECENT_PULLS: RecentPull[] = [
  { id: "rp-1", packId: "baller", agentId: "openai-gpt", finish: "black_ice_prizms", serial: 4, pulledAgo: "2m ago" },
  { id: "rp-2", packId: "premium", agentId: "claude-opus", finish: "black_ice_prizms", serial: 3, pulledAgo: "9m ago" },
  { id: "rp-3", packId: "collector", agentId: "gemini-pro", finish: "red_ice_sapphire", serial: 7, pulledAgo: "14m ago" },
  { id: "rp-4", packId: "starter", agentId: "grok-x", finish: "blue_ice_sapphire", serial: 41, pulledAgo: "21m ago" },
  { id: "rp-5", packId: "collector", agentId: "deepseek-chat", finish: "gold_ice_sapphire", serial: 18, pulledAgo: "36m ago" },
  { id: "rp-6", packId: "starter", agentId: "qwen-3-6-plus", finish: "holo", serial: null, pulledAgo: "48m ago" },
  { id: "rp-7", packId: "premium", agentId: "grok-x", finish: "red_ice_sapphire", serial: 2, pulledAgo: "1h ago" },
  { id: "rp-8", packId: "baller", agentId: "claude-opus", finish: "gold_ice_sapphire", serial: 9, pulledAgo: "3h ago" },
  { id: "rp-9", packId: "quasar", agentId: "gemini-pro", finish: "black_ice_prizms", serial: 1, pulledAgo: "4h ago" },
  { id: "rp-10", packId: "ember", agentId: "openai-gpt", finish: "blue_ice_sapphire", serial: 77, pulledAgo: "5h ago" },
  { id: "rp-11", packId: "comet", agentId: "grok-x", finish: "red_ice_sapphire", serial: 6, pulledAgo: "6h ago" },
];

/** packId 생략 = 전역 피드(TOP PULLS) */
export function recentPulls(packId?: string): RecentPull[] {
  return packId ? RECENT_PULLS.filter((pull) => pull.packId === packId) : RECENT_PULLS;
}

// ------------------------------------------------------------------ Checks

// 확률·가치분포 합 100% 검증 — mock 상수 오타를 로드 시점에 잡는다.
for (const pack of ORIPA_PACKS) {
  const odds = pack.groups.reduce((sum, config) => sum + config.oddsPct, 0);
  const bands = pack.valueBands.reduce((sum, band) => sum + band.pct, 0);
  if (odds !== 100 || bands !== 100) {
    throw new Error(`oripa pack "${pack.id}" odds ${odds}% / bands ${bands}% — must both be 100`);
  }
}
